import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Heart, Package } from "lucide-react";
import api, { formatPrice } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";

const TABS = [
    { id: "orders", label: "Orders" },
    { id: "wishlist", label: "Wishlist" },
    { id: "profile", label: "Profile" },
];

export default function Account() {
    const { user, logout } = useAuth();
    const { wishlist, toggleWishlist, refresh } = useCart();
    const [sp, setSp] = useSearchParams();
    const tab = sp.get("tab") || "orders";
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        api.get("/orders").then(({ data }) => setOrders(data.orders));
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="bg-obsidian" data-testid="account-page">
            <div className="mx-auto max-w-[1480px] px-6 py-12 lg:px-12 lg:py-20">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.4em] text-gold">Hi, {user?.name}</p>
                        <h1 className="mt-3 font-display text-4xl text-white sm:text-5xl">Your atelier</h1>
                    </div>
                    <button
                        onClick={logout}
                        data-testid="account-logout"
                        className="border border-border-dim px-6 py-3 text-[11px] uppercase tracking-[0.25em] text-white/70 hover:border-heart hover:text-heart"
                    >
                        Sign out
                    </button>
                </div>

                <div className="mt-10 flex gap-2 border-b border-border-dim">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setSp({ tab: t.id })}
                            data-testid={`account-tab-${t.id}`}
                            className={`-mb-px border-b-2 px-5 py-3 text-[11px] uppercase tracking-[0.25em] transition-colors ${
                                tab === t.id
                                    ? "border-gold text-gold"
                                    : "border-transparent text-white/60 hover:text-white"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className="mt-10">
                    {tab === "orders" && (
                        <div className="space-y-4">
                            {orders.length === 0 ? (
                                <p className="py-12 text-center text-white/50">No orders yet — your first piece is one click away.</p>
                            ) : (
                                orders.map((o) => (
                                    <Link
                                        to={`/order/${o.id}`}
                                        key={o.id}
                                        data-testid={`order-row-${o.id}`}
                                        className="block border border-border-dim bg-surface p-6 transition-colors hover:border-gold"
                                    >
                                        <div className="flex flex-wrap items-center justify-between gap-4">
                                            <div className="flex items-center gap-4">
                                                <Package className="h-5 w-5 text-gold" />
                                                <div>
                                                    <p className="text-xs uppercase tracking-widest text-white/40">
                                                        Order · {o.id.slice(0, 8)}
                                                    </p>
                                                    <p className="font-display text-xl text-white">
                                                        {o.items.length} piece{o.items.length > 1 ? "s" : ""}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-6">
                                                <StatusBadge status={o.status} />
                                                <p className="font-display text-2xl text-gold">{formatPrice(o.total)}</p>
                                            </div>
                                        </div>
                                    </Link>
                                ))
                            )}
                        </div>
                    )}

                    {tab === "wishlist" && (
                        <div>
                            {wishlist.products.length === 0 ? (
                                <p className="py-12 text-center text-white/50">No favourites saved yet.</p>
                            ) : (
                                <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-4">
                                    {wishlist.products.map((p) => (
                                        <div key={p.id} className="group relative">
                                            <Link to={`/product/${p.id}`}>
                                                <div className="aspect-[4/5] overflow-hidden bg-surface">
                                                    <img
                                                        src={p.images?.[0]}
                                                        alt=""
                                                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                                    />
                                                </div>
                                                <p className="mt-3 font-display text-lg text-white">{p.name}</p>
                                                <p className="text-sm text-gold">{formatPrice(p.price)}</p>
                                            </Link>
                                            <button
                                                onClick={() => toggleWishlist(p.id)}
                                                data-testid={`wishlist-remove-${p.id}`}
                                                className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center bg-obsidian/70 text-heart backdrop-blur"
                                            >
                                                <Heart className="h-4 w-4 fill-heart" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "profile" && (
                        <div className="max-w-lg border border-border-dim bg-surface p-8">
                            <Detail label="Name" value={user?.name} />
                            <Detail label="Email" value={user?.email} />
                            <Detail label="Role" value={user?.role} />
                            <Detail label="Member since" value={(user?.created_at || "").slice(0, 10)} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        pending: "border-white/30 text-white/60",
        paid: "border-gold text-gold",
        processing: "border-gold text-gold",
        shipped: "border-heart text-heart",
        delivered: "border-green-400 text-green-400",
        cancelled: "border-red-400 text-red-400",
    };
    return (
        <span className={`border px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${map[status] || "border-white/30 text-white/60"}`}>
            {status || "pending"}
        </span>
    );
}

function Detail({ label, value }) {
    return (
        <div className="border-b border-border-dim py-4 last:border-b-0">
            <p className="text-[11px] uppercase tracking-[0.25em] text-white/40">{label}</p>
            <p className="mt-1 text-white">{value || "—"}</p>
        </div>
    );
}
